import { useRef, useState } from 'react'
import {
  completionKey,
  extractFocusKey,
  getCustomExerciseImage,
  getExerciseDemo,
  getExerciseDescription,
  getPosterExerciseId,
  isDayComplete,
  muscleTargets,
  parsePrescription,
  posterImage,
  posterSprite,
} from '../lib/exercise'

const thumbScale = 0.5
const detailScale = 1.8

// Distancia (px) a partir de la cual soltar el dedo cierra la solapa.
const CLOSE_DRAG_THRESHOLD = 110
const CLOSE_ANIMATION_MS = 200

function getPosterCrop(exerciseName) {
  const demo = getExerciseDemo(exerciseName)
  const posterExerciseId = getPosterExerciseId(exerciseName, demo.motion)

  if (posterExerciseId === null) return null

  const posterColumn = (posterExerciseId - 1) % 4
  const posterRow = Math.floor((posterExerciseId - 1) / 4)

  return {
    x: posterSprite.startX + posterColumn * (posterSprite.cellWidth + posterSprite.gapX),
    y: posterSprite.startY + posterRow * (posterSprite.cellHeight + posterSprite.gapY),
  }
}

function ExerciseSheet({ date, dayPlan, completion, onToggle, onClose }) {
  const animationKey = extractFocusKey(dayPlan.focus)
  const [detailExercise, setDetailExercise] = useState(null)
  const [dragY, setDragY] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [closing, setClosing] = useState(false)
  const dragStartRef = useRef(null)

  const doneCount = dayPlan.exercises.filter((exercise) => completion[completionKey(date, exercise.name)]).length
  const dayComplete = isDayComplete(dayPlan, date, completion)

  // Deslizar hacia abajo cierra la solapa, pero solo si su contenido esta
  // scrolleado arriba de todo: si no, el gesto scrollea la lista normalmente.
  function handleTouchStart(event) {
    if (closing) {
      return
    }
    const sheet = event.currentTarget
    dragStartRef.current = sheet.scrollTop <= 0 ? event.touches[0].clientY : null
  }

  function handleTouchMove(event) {
    if (dragStartRef.current === null) {
      return
    }
    const delta = event.touches[0].clientY - dragStartRef.current
    if (delta > 0) {
      setDragging(true)
      setDragY(delta)
    } else if (dragging) {
      setDragY(0)
    }
  }

  function handleTouchEnd() {
    if (dragStartRef.current === null) {
      return
    }
    dragStartRef.current = null
    setDragging(false)

    if (dragY > CLOSE_DRAG_THRESHOLD) {
      setClosing(true)
      setDragY(window.innerHeight)
      setTimeout(onClose, CLOSE_ANIMATION_MS)
    } else {
      setDragY(0)
    }
  }

  const sheetStyle =
    dragY > 0 || closing
      ? {
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : `transform ${CLOSE_ANIMATION_MS}ms ease`,
        }
      : undefined

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <article
        className="sheet"
        style={sheetStyle}
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div className="sheet-handle" />

        <header className="sheet-top">
          <div>
            <p className="eyebrow">{date}</p>
            <h2>{dayPlan.focus.split('|')[0].trim()}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Cerrar">
            &#10005;
          </button>
        </header>

        <p className="muscle-targets">Musculos activos: {muscleTargets[animationKey]}</p>

        <div className="sheet-progress">
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${(doneCount / dayPlan.exercises.length) * 100}%` }}
            />
          </div>
          <span>
            {doneCount}/{dayPlan.exercises.length} completados
          </span>
        </div>

        {dayComplete && <p className="day-complete-message">Rutina completada. Buen trabajo, soldado.</p>}

        <ul className="exercise-list">
          {dayPlan.exercises.map((exercise) => {
            const data = parsePrescription(exercise.prescription)
            const key = completionKey(date, exercise.name)
            const done = Boolean(completion[key])
            const customImage = getCustomExerciseImage(exercise.name)
            const crop = customImage ? null : getPosterCrop(exercise.name)
            const description = getExerciseDescription(exercise.name)

            return (
              <li key={exercise.name} className={`exercise-row ${done ? 'done' : ''}`}>
                <label className="exercise-check">
                  <input type="checkbox" checked={done} onChange={() => onToggle(date, exercise.name)} />
                  {customImage ? (
                    <div
                      className="exercise-row-thumb"
                      style={{
                        width: `${posterSprite.cellWidth * thumbScale}px`,
                        height: `${posterSprite.cellHeight * thumbScale}px`,
                      }}
                    >
                      <img
                        src={customImage}
                        alt={`Dibujo de ${exercise.name}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  ) : crop ? (
                    <div
                      className="exercise-row-thumb"
                      style={{
                        width: `${posterSprite.cellWidth * thumbScale}px`,
                        height: `${posterSprite.cellHeight * thumbScale}px`,
                      }}
                    >
                      <img
                        src={posterImage}
                        alt={`Dibujo de ${exercise.name}`}
                        style={{
                          width: `${posterSprite.width * thumbScale}px`,
                          height: `${posterSprite.height * thumbScale}px`,
                          transform: `translate(${-crop.x * thumbScale}px, ${-crop.y * thumbScale}px)`,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="exercise-row-thumb exercise-row-thumb-empty"
                      style={{
                        width: `${posterSprite.cellWidth * thumbScale}px`,
                        height: `${posterSprite.cellHeight * thumbScale}px`,
                      }}
                      aria-hidden="true"
                    />
                  )}
                  <div className="exercise-row-body">
                    <span className="exercise-row-name">{exercise.name}</span>
                    <span className="exercise-row-meta">
                      <strong>{data.series}</strong> series &middot; <strong>{data.reps}</strong> reps/seg
                    </span>
                  </div>
                </label>
                {description && (
                  <button
                    type="button"
                    className="exercise-detail-link"
                    onClick={() => setDetailExercise({ ...exercise, description })}
                  >
                    Ver detalle
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </article>

      {detailExercise && (
        <div className="detail-overlay" onClick={() => setDetailExercise(null)}>
          <article className="detail-modal" onClick={(event) => event.stopPropagation()}>
            <header className="detail-modal-top">
              <h3>{detailExercise.name}</h3>
              <button
                type="button"
                className="icon-button"
                onClick={() => setDetailExercise(null)}
                aria-label="Cerrar"
              >
                &#10005;
              </button>
            </header>
            {(() => {
              const customImage = getCustomExerciseImage(detailExercise.name)
              const crop = customImage ? null : getPosterCrop(detailExercise.name)

              if (customImage) {
                return (
                  <div className="detail-modal-image">
                    <img
                      className="detail-modal-image-fill"
                      src={customImage}
                      alt={`Dibujo de ${detailExercise.name}`}
                    />
                  </div>
                )
              }

              if (crop) {
                return (
                  <div
                    className="detail-modal-image"
                    style={{
                      width: `${posterSprite.cellWidth * detailScale}px`,
                      height: `${posterSprite.cellHeight * detailScale}px`,
                    }}
                  >
                    <img
                      src={posterImage}
                      alt={`Dibujo de ${detailExercise.name}`}
                      style={{
                        width: `${posterSprite.width * detailScale}px`,
                        height: `${posterSprite.height * detailScale}px`,
                        transform: `translate(${-crop.x * detailScale}px, ${-crop.y * detailScale}px)`,
                      }}
                    />
                  </div>
                )
              }

              return null
            })()}
            <p className="detail-modal-prescription">{detailExercise.prescription}</p>
            <p className="detail-modal-description">{detailExercise.description}</p>
          </article>
        </div>
      )}
    </div>
  )
}

export default ExerciseSheet
